import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useBillingUsage, usePaymentMethods } from '@/hooks/useBilling';
import { useUserSubscriptionStatus } from '@/hooks/useSubscriptions';
import { CreditCard, Zap, AlertTriangle, Plus, Crown } from 'lucide-react';

export function PersistentAccountStatusBanner() {
  const navigate = useNavigate();
  const { data: billingUsage } = useBillingUsage();
  const { data: paymentMethods, isLoading: paymentMethodsLoading } = usePaymentMethods();
  const { data: subscriptionStatus } = useUserSubscriptionStatus();
  
  // Don't show anything while loading
  if (!billingUsage || paymentMethodsLoading) {
    return null;
  }

  const hasPaymentMethod = paymentMethods?.savedMethods && paymentMethods.savedMethods.length > 0;
  const isDelinquent = billingUsage.isDelinquent;
  const isLowCredits = billingUsage.creditsRemaining < 10 && billingUsage.creditsRemaining > 0;
  const hasAssignedPlan = subscriptionStatus?.needs_stripe_subscription && subscriptionStatus?.has_plan_assignment;

  // Priority hierarchy:
  // 1. Assigned plan needs activation (partner assigned a plan)
  // 2. No payment method (blocks everything)
  // 3. Delinquent account (service stopped)
  // 4. Low credits (warning)

  const handleAddPaymentMethod = () => {
    navigate('/dashboard/billing?tab=payment-methods');
  };

  const handleRestoreService = () => {
    navigate('/dashboard/billing?tab=boost-packs');
  };

  const handleActivateSubscription = () => {
    navigate('/dashboard/billing?tab=subscription-plans');
  };

  // Priority 1: Assigned plan needs activation (partner assigned a plan but user hasn't paid)
  if (hasAssignedPlan) {
    return (
      <div className="bg-purple-50 border-b border-purple-200 dark:bg-purple-900/20 dark:border-purple-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <Crown className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-purple-800 dark:text-purple-200">
                  <span className="font-semibold">Plan Assigned</span> — Your partner has assigned you a subscription plan. Activate it to start using your AI Employee
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-xs text-purple-700 dark:text-purple-300 hidden sm:block">
                Activate now →
              </span>
              <Button 
                onClick={handleActivateSubscription}
                size="sm"
                className="bg-purple-600 hover:bg-purple-700 text-white border-0 font-medium"
              >
                <Crown className="h-3 w-3 mr-1.5" />
                Activate Plan
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Priority 2: No payment method (show regardless of credit status)
  if (!hasPaymentMethod) {
    return (
      <div className="bg-blue-50 border-b border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  <span className="font-semibold">Setup Required</span> — Add a payment method to unlock your AI Employee's full potential
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-xs text-blue-700 dark:text-blue-300 hidden sm:block">
                Quick setup →
              </span>
              <Button 
                onClick={handleAddPaymentMethod}
                size="sm"
                className="bg-primary hover:bg-primary/90 text-primary-foreground border-0 font-medium"
              >
                <Plus className="h-3 w-3 mr-1.5" />
                Add Payment Method
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Priority 3: Delinquent account (has payment method but no credits)
  if (isDelinquent) {
    return (
      <div className="bg-red-50 border-b border-red-200 dark:bg-red-900/20 dark:border-red-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  <span className="font-semibold">Service Suspended</span> — Your AI Employee needs credits to continue working for you
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-xs text-red-700 dark:text-red-300 hidden sm:block">
                2-minute fix →
              </span>
              <Button 
                onClick={handleRestoreService}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white border-0 font-medium"
              >
                <Zap className="h-3 w-3 mr-1.5" />
                Restore Service
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Priority 4: Low credits (has payment method, service active, but running low)
  if (isLowCredits) {
    return (
      <div className="bg-amber-50 border-b border-amber-200 dark:bg-amber-900/20 dark:border-amber-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <CreditCard className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  <span className="font-semibold">Running Low</span> — {billingUsage.creditsRemaining.toFixed(1)} credits remaining. Add more to keep your AI Employee active
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-xs text-amber-700 dark:text-amber-300 hidden sm:block">
                Prevent interruption →
              </span>
              <Button 
                onClick={handleRestoreService}
                variant="outline"
                size="sm"
                className="border-amber-600 text-amber-700 hover:bg-amber-100 dark:border-amber-400 dark:text-amber-400 font-medium"
              >
                <CreditCard className="h-3 w-3 mr-1.5" />
                Add Credits
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // User has payment method and sufficient credits - no banner needed
  return null;
} 